const PROVIDER_LABELS = Object.freeze({
  aws: "AWS",
  azure: "Azure",
  gcp: "GCP",
  oci: "OCI",
});

export const CLOUD_SERVICE_CATALOG = Object.freeze({
  aws: [
    "EC2", "S3", "RDS", "IAM", "VPC", "Lambda", "CloudTrail",
    "CloudWatch", "ELB", "EKS", "KMS", "Route 53", "Secrets Manager",
  ],
  azure: [
    "Virtual Machines", "Blob Storage", "SQL Database", "Microsoft Entra ID",
    "Virtual Network", "Network Security Groups", "Functions", "AKS",
    "Key Vault", "Monitor", "Load Balancer",
  ],
  gcp: [
    "Compute Engine", "Cloud Storage", "Cloud SQL", "IAM", "VPC",
    "Firewall", "Cloud Functions", "GKE", "Cloud DNS", "BigQuery",
    "Secret Manager", "Cloud Logging",
  ],
  oci: [
    "Compute", "Object Storage", "VCN", "Security Lists", "NSG", "IAM",
    "Load Balancer", "Autonomous Database", "Vault", "Logging", "Monitoring",
    "OKE",
  ],
});

const RESOURCE_COLLECTIONS = /(?:^|_)(instances?|buckets?|databases?|clusters?|functions?|load_?balancers?|networks?|vpcs?|subnets?|volumes?|vaults?|keys?|secrets?|repositories|topics|queues|users?|roles?|groups?|policies|security_?groups?|network_?interfaces?|storage_?accounts?|virtual_?machines?|resource_?groups?|projects?|compartments?|tenancies)$/i;
const SKIPPED_BRANCHES = new Set([
  "findings", "filters", "compliance", "references", "permissions",
  "rules", "metadata", "last_run", "summary", "service_groups",
]);
const IDENTIFIER_KEYS = [
  "id", "name", "arn", "ocid", "resource_id", "resourceId", "self_link",
  "selfLink", "resource_name", "resourceName", "display_name", "displayName",
  "BucketArn", "instance_id", "instanceId",
];
const REGION_KEYS = ["region", "location", "zone", "availability_zone", "availabilityZone"];
const PUBLIC_IP_KEYS = /^(?:public_?ip(?:_address)?|external_?ip(?:_address)?|nat_?ip|publicIpAddress|ip_address)$/i;
const URL_KEYS = /(?:^|_)(?:url|uri|endpoint|website_endpoint|website_url|public_url|self_link)$/i;

export const parseCloudJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const asArray = (value) => (Array.isArray(value) ? value : value == null ? [] : [value]);

const titleCase = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const numberFrom = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (value !== "" && value != null && Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

export const normalizeSeverity = (value, flagged = 0) => {
  const severity = String(value || "").toLowerCase();
  if (["danger", "critical", "urgent"].includes(severity)) return "Critical";
  if (["warning", "high", "important"].includes(severity)) return "High";
  if (["medium", "moderate"].includes(severity)) return "Medium";
  if (["low", "info", "informational", "none"].includes(severity)) return "Low";
  return flagged > 0 ? "Review" : "Low";
};

const unwrapReports = (source) =>
  asArray(parseCloudJson(source))
    .map((record) => parseCloudJson(record?.misconfig ?? record))
    .filter((record) => record && typeof record === "object");

export const normalizeServiceRows = (source, providerLabel) =>
  unwrapReports(source).flatMap((report, reportIndex) => {
    const summary = parseCloudJson(report?.last_run?.summary ?? report?.summary);
    if (!summary || typeof summary !== "object") return [];
    const entries = Array.isArray(summary)
      ? summary.map((item, index) => [item?.name || item?.service || `Check ${index + 1}`, item])
      : Object.entries(summary);

    return entries
      .map(([key, rawItem], index) => {
        const item = rawItem && typeof rawItem === "object" ? rawItem : { flagged_items: rawItem };
        const checked = numberFrom(item.checked_items, item.checkedItems, item.checked);
        const flagged = numberFrom(item.flagged_items, item.flaggedItems, item.failed, item.issues);
        return {
          id: `${providerLabel}-${reportIndex}-${key}-${index}`,
          provider: providerLabel,
          name: item.name || item.title || titleCase(key),
          service: item.service || item.category || titleCase(key),
          checked,
          flagged,
          resources: numberFrom(item.resources_count, item.resourcesCount, item.resources),
          rules: numberFrom(item.rules_count, item.rulesCount, item.rules),
          level: String(item.max_level || item.level || "").toLowerCase(),
          severity: normalizeSeverity(item.max_level || item.level, flagged),
        };
      })
      .filter((row) => row.checked || row.flagged || row.resources || row.rules);
  });

const isIpv4 = (value) => {
  const parts = String(value || "").trim().split(".");
  return parts.length === 4 && parts.every((part) => /^\d+$/.test(part) && Number(part) <= 255);
};

export const isPublicIpv4 = (value) => {
  if (!isIpv4(value)) return false;
  const [a, b, c] = String(value).split(".").map(Number);
  return !(
    a === 0 || a === 10 || a === 127 ||
    (a === 100 && b >= 64 && b <= 127) ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 0 && c === 0) ||
    (a === 192 && b === 0 && c === 2) ||
    (a === 192 && b === 88 && c === 99) ||
    (a === 192 && b === 168) ||
    (a === 198 && (b === 18 || b === 19)) ||
    (a === 198 && b === 51 && c === 100) ||
    (a === 203 && b === 0 && c === 113) ||
    a >= 224
  );
};

const collectScalarEvidence = (node, matcher, maxDepth = 3, depth = 0, values = []) => {
  if (!node || typeof node !== "object" || depth > maxDepth) return values;
  Object.entries(node).forEach(([key, value]) => {
    if (typeof value === "string" && matcher(key, value)) values.push(value.trim());
    else if (value && typeof value === "object") {
      collectScalarEvidence(value, matcher, maxDepth, depth + 1, values);
    }
  });
  return values;
};

const extractPublicIps = (node) => [
  ...new Set(collectScalarEvidence(node, (key, value) => PUBLIC_IP_KEYS.test(key) && isPublicIpv4(value))),
];

const extractUrls = (node) => [
  ...new Set(
    collectScalarEvidence(node, (key, value) => URL_KEYS.test(key) && /^https?:\/\//i.test(value))
  ),
];

const resourceIdentifier = (node) => {
  const key = IDENTIFIER_KEYS.find((candidate) => {
    const value = node?.[candidate];
    return typeof value === "string" && value.trim();
  });
  return key ? String(node[key]).trim() : "";
};

const resourceRegion = (node, inheritedRegion) => {
  const key = REGION_KEYS.find((candidate) => typeof node?.[candidate] === "string" && node[candidate]);
  return key ? node[key] : inheritedRegion || "Global";
};

const singular = (value = "resource") => {
  const normalized = titleCase(value);
  if (/ies$/i.test(normalized)) return normalized.replace(/ies$/i, "y");
  if (/sses$/i.test(normalized)) return normalized.replace(/es$/i, "");
  if (/s$/i.test(normalized) && !/ss$/i.test(normalized)) return normalized.slice(0, -1);
  return normalized;
};

export const extractCloudAssets = (source, providerId) => {
  const provider = PROVIDER_LABELS[providerId] || titleCase(providerId);
  const assets = [];

  unwrapReports(source).forEach((report, reportIndex) => {
    Object.entries(report.services || {}).forEach(([serviceKey, serviceValue]) => {
      const walk = (node, path, parentKey, inheritedRegion, depth) => {
        if (!node || typeof node !== "object" || depth > 10) return;
        const currentRegion = resourceRegion(node, inheritedRegion);
        const identifier = resourceIdentifier(node);
        const collectionKey = path.length > 1 ? path[path.length - 2] : parentKey;
        const isCollectionChild = RESOURCE_COLLECTIONS.test(collectionKey || "");

        if (identifier && isCollectionChild) {
          const publicIps = extractPublicIps(node);
          const endpoints = extractUrls(node);
          assets.push({
            id: `${providerId}-${reportIndex}-${path.join(".")}-${identifier}`,
            provider,
            providerId,
            service: titleCase(serviceKey),
            type: singular(collectionKey),
            name: String(node.name || node.display_name || node.displayName || identifier),
            resourceId: identifier,
            region: currentRegion,
            publicIps,
            endpoints,
            exposed: publicIps.length > 0 || endpoints.length > 0,
            evidencePath: path.join("."),
            raw: node,
          });
        }

        Object.entries(node).forEach(([key, value]) => {
          if (SKIPPED_BRANCHES.has(key) || key.endsWith("_count")) return;
          if (value && typeof value === "object") {
            walk(value, [...path, key], key, currentRegion, depth + 1);
          }
        });
      };
      walk(serviceValue, [serviceKey], serviceKey, "Global", 0);
    });
  });

  const seen = new Set();
  return assets.filter((asset) => {
    const key = `${asset.providerId}:${asset.service}:${asset.resourceId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeCompliance = (value) =>
  asArray(value)
    .filter(Boolean)
    .map((control) =>
      typeof control === "string"
        ? { framework: control, control: "Unspecified", version: "" }
        : {
            framework: control.name || control.framework || "Unspecified framework",
            control: String(control.reference || control.control || control.id || "Unspecified"),
            version: String(control.version || ""),
          }
    );

export const extractConfigurationFindings = (source, providerId) => {
  const provider = PROVIDER_LABELS[providerId] || titleCase(providerId);
  return unwrapReports(source).flatMap((report, reportIndex) =>
    Object.entries(report.services || {}).flatMap(([serviceKey, serviceValue]) =>
      Object.entries(serviceValue?.findings || {})
        .map(([findingKey, finding], index) => {
          const flagged = numberFrom(finding?.flagged_items, finding?.flaggedItems);
          if (!flagged) return null;
          const evidencePaths = asArray(finding?.items).filter((item) => typeof item === "string");
          const severity = normalizeSeverity(finding?.level, flagged);
          return {
            id: `${providerId}-config-${reportIndex}-${findingKey}-${index}`,
            contextType: "cloud_finding",
            findingType: "Misconfiguration",
            provider,
            providerId,
            service: finding?.service || titleCase(serviceKey),
            findingName: finding?.description || titleCase(findingKey),
            alert: finding?.description || titleCase(findingKey),
            ruleId: findingKey,
            severity,
            flagged,
            checked: numberFrom(finding?.checked_items, finding?.checkedItems),
            rationale: finding?.rationale || "No rationale was returned by the assessment provider.",
            remediation: finding?.remediation || "Review the provider configuration and apply least privilege.",
            compliance: normalizeCompliance(finding?.compliance),
            references: asArray(finding?.references).filter(Boolean),
            evidencePaths,
            dashboardName: finding?.dashboard_name || "Configuration",
            status: "Open",
            cvss: null,
            epss: null,
            exploitAvailable: null,
          };
        })
        .filter(Boolean)
    )
  );
};

const normalizeScannerFindings = (results, providerId, findingType) => {
  const provider = PROVIDER_LABELS[providerId] || titleCase(providerId);
  return asArray(results).flatMap((scan, scanIndex) => {
    const vulnerabilities = parseCloudJson(scan?.vuln_data) || [];
    const epssRows = parseCloudJson(scan?.epss_data) || [];
    const target = findingType === "Network" ? scan?.ip_address || scan?.ip : scan?.url;
    return asArray(vulnerabilities).map((finding, index) => {
      const cveId = String(finding?.codes || finding?.cve || finding?.cveId || "").split(/[ ,]/)[0];
      const epssMatch = asArray(epssRows).find((row) =>
        cveId && JSON.stringify(row).toUpperCase().includes(cveId.toUpperCase())
      );
      const severity = normalizeSeverity(finding?.baseSeverity || finding?.risk || finding?.severity, 1);
      const rationale =
        finding?.description ||
        finding?.analysis ||
        "Detected by an existing SAVE scanner.";
      const remediation = Array.isArray(finding?.solution)
        ? finding.solution.join(" ")
        : finding?.solution ||
          "Review the scanner evidence and remediate the affected target.";
      const scanId = scan?.scan_uuid || scan?.scanId || `${providerId}-${scanIndex + 1}`;
      return {
        id: `${providerId}-${findingType.toLowerCase()}-${scanId}-${index}`,
        contextType: findingType === "Network" ? "network_finding" : "finding",
        findingType: `${findingType} Vulnerability`,
        provider,
        providerId,
        service: findingType === "Network" ? finding?.type || "Network" : "Web",
        findingName: finding?.title || finding?.name || finding?.alert || "Scanner finding",
        alert: finding?.alert || finding?.title || finding?.name,
        severity,
        flagged: 1,
        checked: 1,
        rationale,
        remediation,
        compliance: [],
        references: asArray(finding?.reference || finding?.references).filter(Boolean),
        evidencePaths: target ? [String(target)] : [],
        target: target || "Unknown target",
        risk: finding?.risk || severity,
        url: findingType === "Web" ? target || "Unknown target" : undefined,
        description: rationale,
        solution: remediation,
        evidence: finding?.evidence || finding?.proof || finding?.request || "Not reported",
        method: finding?.method,
        param: finding?.param,
        confidence: finding?.confidence,
        cweid: finding?.cweid || finding?.cweId,
        wascid: finding?.wascid || finding?.wascId,
        pluginId: finding?.pluginId || finding?.pluginid,
        source: finding?.source || `SAVE ${findingType.toLowerCase()} scanner`,
        detectedBy: finding?.detectedBy || `SAVE ${findingType.toLowerCase()} scanner`,
        port: finding?.port,
        cveId: /^CVE-/i.test(cveId) ? cveId : "",
        codes: finding?.codes || (/^CVE-/i.test(cveId) ? cveId : ""),
        asset: findingType === "Network" ? target || "Unknown target" : undefined,
        vulnerabilityType: finding?.type || findingType,
        platform: finding?.platform || "Not reported",
        exploitability: finding?.exploitability ?? finding?.exploit ?? "Not reported",
        impact: finding?.impact ?? "Not reported",
        scannerSource: finding?.source || `SAVE ${findingType.toLowerCase()} scanner`,
        scanId,
        totalFindings: asArray(vulnerabilities).length,
        priorityRank: index + 1,
        priorityBand: severity,
        cvss: finding?.cvss || finding?.cvssScore || finding?.baseScore || null,
        epss: epssMatch?.epss || epssMatch?.score || finding?.epss || null,
        exploitAvailable: finding?.exploitability || finding?.exploit || null,
        status: "Open",
      };
    });
  });
};

export const buildComplianceControls = (findings) => {
  const controls = new Map();
  findings.forEach((finding) => {
    finding.compliance.forEach((control) => {
      const key = `${control.framework}|${control.version}|${control.control}`;
      const current = controls.get(key) || { ...control, failedFindings: 0, services: new Set(), providers: new Set() };
      current.failedFindings += finding.flagged || 1;
      current.services.add(finding.service);
      current.providers.add(finding.provider);
      controls.set(key, current);
    });
  });
  return [...controls.values()].map((control) => ({
    ...control,
    status: "Failed",
    services: [...control.services],
    providers: [...control.providers],
  }));
};

const findingLooksExposed = (finding) =>
  /public|internet|world|anonymous|0\.0\.0\.0|open (?:port|access|security)|externally accessible/i.test(
    `${finding.findingName} ${finding.rationale} ${finding.evidencePaths.join(" ")}`
  );

export const buildAttackPaths = (findings, assets) => {
  const paths = [];
  findings.forEach((finding) => {
    const linkedAsset = assets.find((asset) =>
      asset.providerId === finding.providerId &&
      (finding.evidencePaths.some((path) => path.includes(asset.resourceId)) ||
        (asset.service.toLowerCase() === finding.service.toLowerCase() && asset.exposed))
    );
    const isScannerExposure = ["Network Vulnerability", "Web Vulnerability"].includes(finding.findingType);
    if (!isScannerExposure && !findingLooksExposed(finding) && !linkedAsset?.exposed) return;
    const target = finding.target || linkedAsset?.name || finding.service;
    paths.push({
      id: `path-${finding.id}`,
      provider: finding.provider,
      severity: finding.severity,
      title: `${target} exposure path`,
      steps: ["Internet", target, finding.findingName],
      evidence: finding.evidencePaths,
      finding,
    });
  });
  return paths;
};

export const buildCloudEvidence = ({
  providerId,
  configSource,
  networkResults = [],
  webResults = [],
}) => {
  const serviceRows = normalizeServiceRows(configSource, PROVIDER_LABELS[providerId] || providerId);
  const assets = extractCloudAssets(configSource, providerId);
  const findings = [
    ...extractConfigurationFindings(configSource, providerId),
    ...normalizeScannerFindings(networkResults, providerId, "Network"),
    ...normalizeScannerFindings(webResults, providerId, "Web"),
  ];
  const targets = {
    publicIps: [...new Set(assets.flatMap((asset) => asset.publicIps))],
    endpoints: [...new Set(assets.flatMap((asset) => asset.endpoints))],
  };
  return {
    serviceRows,
    assets,
    findings,
    compliance: buildComplianceControls(findings),
    attackPaths: buildAttackPaths(findings, assets),
    targets,
  };
};

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const cloudFindingsCsv = (findings) => {
  const headers = [
    "Provider", "Type", "Service", "Finding", "Severity", "Affected Count",
    "Target", "CVSS", "EPSS", "Rationale", "Remediation", "Evidence",
  ];
  const rows = findings.map((finding) => [
    finding.provider, finding.findingType, finding.service, finding.findingName,
    finding.severity, finding.flagged, finding.target || "", finding.cvss || "",
    finding.epss || "", finding.rationale, finding.remediation,
    finding.evidencePaths.join(" | "),
  ].map(csvCell).join(","));
  return [headers.map(csvCell).join(","), ...rows].join("\n");
};

export const downloadTextFile = (content, filename, type = "text/plain;charset=utf-8") => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
